/**
 * Priority: danger > 0 → danger tier; else safe > 0 → safe; else unknown > 0 → unknown; else neutral.
 */
export type RoomStatusTier = 'danger' | 'safe' | 'unknown' | 'neutral';

/** Room tint when an active alert targets the room (lower priority than user status). */
export type RoomTintTier = RoomStatusTier | 'alert';

export const ALERT_ROOM_COLOR_HEX = '#ff9933';

export interface RoomStatusCounts {
  danger: number;
  safe: number;
  unknown: number;
}

export function resolveRoomStatusTier(counts: RoomStatusCounts): RoomStatusTier {
  const d = counts.danger ?? 0;
  const s = counts.safe ?? 0;
  const u = counts.unknown ?? 0;
  if (d === 0 && s === 0 && u === 0) {
    return 'neutral';
  }
  if (d >= s && d >= u) {
    return 'danger';
  }
  if (s >= d && s >= u) {
    return 'safe';
  }
  return 'unknown';
}

/** Light red / light green / light yellow (diffuse-friendly sRGB) — default when no overrides */
export const DEFAULT_ROOM_STATUS_COLOR_HEX: Record<Exclude<RoomStatusTier, 'neutral'>, string> = {
  danger: '#ffb3b3',
  safe: '#b8e6b8',
  unknown: '#fad502',
};

/** @deprecated use DEFAULT_ROOM_STATUS_COLOR_HEX */
export const ROOM_STATUS_COLOR_HEX = DEFAULT_ROOM_STATUS_COLOR_HEX;

/** Optional hex overrides per tier (omit keys to keep defaults) */
export type ViewerRoomStatusColors = Partial<Record<Exclude<RoomStatusTier, 'neutral'>, string>>;

export function mergeRoomStatusColors(
  overrides: ViewerRoomStatusColors | null | undefined
): Record<Exclude<RoomStatusTier, 'neutral'>, string> {
  return {
    danger: overrides?.danger ?? DEFAULT_ROOM_STATUS_COLOR_HEX.danger,
    safe: overrides?.safe ?? DEFAULT_ROOM_STATUS_COLOR_HEX.safe,
    unknown: overrides?.unknown ?? DEFAULT_ROOM_STATUS_COLOR_HEX.unknown,
  };
}
